import React, { useEffect, useMemo, useState } from "react";
import {
  ActionKey,
  ActionLabel,
  DeleteLabel,
  editLabel,
  NewLabel,
} from "Components/constants/Role";
import TableContainer from "Components/Base/BaseTable";
import BaseButton from "Components/Base/BaseButton";
import {
  checkStatusCodeSuccess,
  DownloadLabel,
  notFound,
  pdfDownload,
  rupeeSymbol,
  Search,
} from "Components/constants/Common";
import { useNavigate, useParams } from "react-router-dom";
import { listOfProject } from "Api/Project";
import { toast } from "react-toastify";
import { RiSearchLine } from "react-icons/ri";
import { dummyData, TaxInvoiceKey, TaxInvoiceLabel } from "Components/constants/taxInvoice";
import { listOfTaxInvoice, taxInvoicePdfApi } from "Api/taxInvoice";
import Loader from "Components/Base/BaseLoader";

const TaxInvoice = () => {
  document.title = TaxInvoiceLabel.TaxInvoiceTitle;
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [taxInvoice, setTaxInvoice] = useState<any[]>([]);
  const [searchTaxInvoice, setSearchTaxInvoice] = useState<
    Array<any>
  >([]);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [columnName, setColumnName] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");
  const [loader, setLoader] = useState<boolean>(false);
  const [pageLoader, setPageLoader] = useState<boolean>(false);
  const navigate = useNavigate();
  const { workOrderId } = useParams();

  const fetchTaxInvoice = async () => {
    const payload = {
      pageSize,
      page: currentPage,
      sortKey: columnName,
      sortValue: sortOrder,
    };
    setLoader(true);
    listOfTaxInvoice(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setTaxInvoice(res?.data?.TaxInvoice || []);
          setTotalRecords(res?.data?.totalItems || 0);
          setTotalPages(res?.data?.totalPages);
          setCurrentPage(res?.data?.currentPage);
          setTotalNumberOfRows(res?.data?.numberOfRows);
          toast.success(res?.message);
        } else {
          setTaxInvoice([]);
        }
      })
      .catch(() => {
        setTaxInvoice([]);
      })
      .finally(() => setLoader(false));
  };

  const fetchSearchTaxInvoiceData = () => {
    const payload = {
      pageSize,
      page: currentPage,
      search: searchValue,
      sortKey: columnName,
      sortValue: sortOrder,
    };
    setLoader(true);
    listOfTaxInvoice(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setSearchTaxInvoice(res?.data?.TaxInvoice || []);
          setTotalRecords(res?.data?.totalItems || 0);
          setTotalPages(res?.data?.totalPages);
          setCurrentPage(res?.data?.currentPage);
          setTotalNumberOfRows(res?.data?.numberOfRows);
          toast.success(res?.message);
        } else {
          setSearchTaxInvoice([]);
        }
      })
      .catch(() => {
        setSearchTaxInvoice([]);
      })
      .finally(() => setLoader(false));
  };

  useEffect(() => {
    if (searchValue) {
      fetchSearchTaxInvoiceData();
    } else {
      fetchTaxInvoice();
    }
  }, [currentPage, pageSize, searchValue, sortOrder, columnName]);

  const handleDownload = (row:any) => {
    pdfDownload(
      taxInvoicePdfApi,
      row?.id,
      `taxinvoice_${row?.tax_in_no}.pdf`,
      setPageLoader
    );
  };

  const taxInvoiceColumns = useMemo(
    () => [
      {
        header: TaxInvoiceLabel.VendorId,
        accessorKey: TaxInvoiceKey.VendorID,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: TaxInvoiceLabel.VendorName,
        accessorKey: TaxInvoiceKey.VendorName,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: TaxInvoiceLabel.TaxInvoice,
        accessorKey: TaxInvoiceKey.TaxInvoice,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: TaxInvoiceLabel.TaxInvoiceDT,
        accessorKey: TaxInvoiceKey.TaxInvoiceDT,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: TaxInvoiceLabel.TaxableValue,
        accessorKey: TaxInvoiceKey.TaxableValue,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
            return rupeeSymbol(cell?.row?.original?.taxable);
        },
      },
      {
        header: TaxInvoiceLabel.GST,
        accessorKey: TaxInvoiceKey.GST,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
            return rupeeSymbol(cell?.row?.original?.gst);
        },
      },
      {
        header: TaxInvoiceLabel.TaxInvoiceAmt,
        accessorKey: TaxInvoiceKey.TaxInvoiceAmt,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
            return rupeeSymbol(cell?.row?.original?.tax_invoice_amount);
        },
      },
      {
        header: TaxInvoiceLabel.Warranty,
        accessorKey: TaxInvoiceKey.Warranty,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: ActionLabel,
        accessorKey: ActionKey,
        cell: (cell: any) => {
          return (
            <div className="d-flex justify-content-start enable-action flex-nowrap gap-2 icon">
              <span className="tooltip-container">
                <i
                  className={`mdi mdi-download-outline fs-4 text-primary cursor-pointer`}
                  onClick={() => handleDownload(cell?.row?.original)}
                ></i>
                <span className="tooltip-text">{DownloadLabel}</span>
              </span>
              <span className="tooltip-container">
                <i
                  className={`${""} mdi mdi-pencil-outline fs-4 text-info cursor-pointer`}
                  onClick={() => navigate(`/edit-tax-invoice/${cell.row.original.id}`)}
                ></i>
                <span className="tooltip-text">{editLabel}</span>
              </span>
            </div>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    []
  );

  const fetchData = (page: number) => {
    setCurrentPage(page);
  };

  const fetchSortingData = (page: number, id: string, order: string) => {
    setCurrentPage(page);
    setColumnName(id);
    setSortOrder(order);
  };

  const handleSearch = (value: string) => {
    if (value !== searchValue) {
      setCurrentPage(1);
      setSearchValue(value);
    }
  };

  return (
    <div className="page-content">
      {pageLoader && <Loader/>}
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4 d-flex gap-4">{TaxInvoiceLabel.TaxInvoice} </span>
          <div className="d-flex align-items-center gap-2">
            <BaseButton
              className="btn btn-success"
              onClick={() => navigate("/add-tax-invoice")}
              children={
                <>
                  <i className="mdi mdi-plus" /> {NewLabel}
                </>
              }
            />
          </div>
        </div>
        <div className="card py-2 px-4 overflow-x-auto">
          {loader || taxInvoice?.length > 0 ? (
            <TableContainer
              customPageSize={pageSize}
              setCustomPageSize={setPageSize}
              isGlobalFilter={true}
              columns={taxInvoiceColumns}
              data={searchValue ? searchTaxInvoice : taxInvoice || []}
              tableClass="table bg-white"
              manualPagination={true}
              manualFiltering={true}
              tableLoader={loader}
              hasManualPagination={true}
              SearchPlaceholder={Search}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              onSearch={handleSearch}
              totalNumberOfRows={totalNumberOfRows}
              fetchData={fetchData}
              fetchSortingData={fetchSortingData}
            />
          ) : (
            <div className="py-4 text-center">
              <div>
                <span className="fs-1 text-success">
                  <RiSearchLine />
                </span>
              </div>
              <div className="mt-4">
                <h5>{notFound.dataNotFound}</h5>
                <p className="text-muted">{notFound.thereIsNoDataFound}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaxInvoice;
